import { MyRewardControllerService } from '@/__generated__/FrontApi';
import Container from '@/components/Container';
import LinkSafe from '@/components/LinkSafe';
import ListSkeleton from '@/components/Mypage/ListSkeleton';
import MyRewardAddressItem, {
  RewardAddressPostParams,
  RewardAddress100DaysPostParams,
} from '@/components/Mypage/MyRewardAddressItem';
import { NotiBox, NotiList, NotiItem } from '@/components/Text/TextNoti';
import { breakpoint } from '@/helpers/BreakpointHelper';
import LayoutWithTitle from '@/layouts/LayoutWithTitle';
import { ContainerGroup, SectionTop } from '@/page-blocks/mypage/MypageCommon';
import MypageRoutes from '@/page-blocks/mypage/MypageRoutes';
import { usePopupStore } from '@/stores/PopupStore';
import { useLocation } from '@reach/router';
import axios from 'axios';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

const NoData = styled.div`
  text-align: center;
  font-size: 20px;
  font-weight: bold;
  padding: 96px 16px;
  ${breakpoint(800)} {
    font-size: 16px;
  }
  ${breakpoint(640)} {
    font-size: 14px;
    padding: 72px 16px;
  }
`;

// 발송현황 인터페이스
export interface MyRewardVo {
  giftId: string;
  zipCode: string;
  address: string;
  addressDetail: string;
  addressEditYn: 'Y' | 'N';
  address100DaysEditYn: 'Y' | 'N';
  // donorName: string;
  solicitCode: string;
  // 250317 누락 추가
  description?: string;
  rewardName?: string;
  recordType?: string;
  authKey?: string;
  donationStatus?: string;
  donationPaidDate?: string;
  shippingDate?: string;
  shippingDateDisplayYn?: string;
  etcOpt?: string;
  // 250317 신규 추가
  fourthKakaoYn?: string;
  fourthMessage?: string;
  zipcode100days?: string;
  addr1100days?: string;
  addr2100days?: string;
  // 260113 SF 관련 신규 추가
  rewardOrder?: string;
  deliveryState?: string;
  rewardId: string;
  rewardOptionId?: string;
  sfRewardProductOptList?: any[];
}

const MyReward: React.FC = observer(() => {
  const location = useLocation();
  const popupStore = usePopupStore();
  const [items, setItems] = useState<MyRewardVo[]>([]);
  const [loading, setLoading] = useState(true);

  // 발송현황 로드
  const loadItems = useCallback(async () => {
    try {
      const {
        resultCode,
        data,
      } = await MyRewardControllerService.getMyRewardListUsingGet();
      if (resultCode === `success`) {
        const listData = data as MyRewardVo[];
        setItems(listData);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleItemChange = useCallback(
    async (params: RewardAddressPostParams) => {
      try {
        const {
          resultCode,
          resultMessage,
        } = await MyRewardControllerService.postMyAddressEditUsingPost(params);
        if (resultCode === `success`) {
          // 데이터 업데이트
          loadItems();
          return true;
        }

        popupStore.show(resultMessage || '');
      } catch (e) {
        if (axios.isAxiosError(e)) {
          popupStore.show(
            e.response?.data.resultMessage || `로딩 중 오류가 발생했습니다`,
          );
        } else {
          popupStore.show(`로딩 중 오류가 발생했습니다`);
        }
      }
      return false;
    },
    [loadItems, popupStore],
  );

  const handle100DaysItemChange = useCallback(
    async (params: RewardAddress100DaysPostParams) => {
      try {
        const {
          resultCode,
          resultMessage,
        } = await MyRewardControllerService.postMyAddress100DaysEditUsingPost(
          params,
        );
        if (resultCode === `success`) {
          // 데이터 업데이트
          loadItems();
          return true;
        }

        popupStore.show(resultMessage || '');
      } catch (e) {
        if (axios.isAxiosError(e)) {
          popupStore.show(
            e.response?.data.resultMessage || `저장중 오류가 발생했습니다`,
          );
        } else {
          popupStore.show(`저장중 오류가 발생했습니다`);
        }
      }

      return false;
    },
    [loadItems, popupStore],
  );

  return (
    <LayoutWithTitle
      title="마이페이지"
      location={location}
      description="for every child, UNICEF"
    >
      <MypageRoutes currentKey={6} />
      <SectionTop>
        <ContainerGroup>
          <Container>
            <NotiBox>
              <NotiList>
                <NotiItem>
                  굿즈(반지, 팔찌 등)는 첫 후원금 납입 후 일반우편으로 순차
                  발송됩니다.
                </NotiItem>
                <NotiItem>
                  발송 예정일 1-2주(영업일 기준) 후 우편함에서 수령하실 수
                  있습니다.
                </NotiItem>
                <NotiItem>
                  일시후원만 신청하시거나 후원금이 정상적으로 납입되지 않은 경우
                  발송이 제한될 수 있습니다.
                </NotiItem>
                <NotiItem>
                  발송 관련 문의는 <a href="/mypage/onetoone">1:1 게시판</a>으로
                  연락 부탁드리며, 기타 문의사항은 <a href="/faq">FAQ</a>를
                  확인해 주세요.
                </NotiItem>
              </NotiList>
            </NotiBox>
          </Container>
          <Container>
            <ul>
              {loading && <ListSkeleton />}
              {!loading && items.length === 0 && (
                <NoData>발송현황이 존재하지 않습니다.</NoData>
              )}
              {!loading &&
                items.map((item) => (
                  <MyRewardAddressItem
                    key={item.rewardId}
                    item={item}
                    onItemChange={handleItemChange}
                    on100DaysItemChange={handle100DaysItemChange}
                  />
                ))}
            </ul>
          </Container>
        </ContainerGroup>
      </SectionTop>
    </LayoutWithTitle>
  );
});

export default MyReward;
